import { useEffect, useRef, useState } from "react";
import { useAuth, useClerk } from "@clerk/react";
import { getPartnerApp } from "../utils/partnerApp";

function record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function PartnerConnectionCheck() {
  const { getToken } = useAuth();
  const clerk = useClerk();
  const busy = useRef(false);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  const [code, setCode] = useState("");

  const partnerApp = getPartnerApp();
  const applicationKey = partnerApp === "laboratory" ? "lab" : partnerApp;

  useEffect(() => {
    void test();
  }, []);

  async function test() {
    if (busy.current) return;
    busy.current = true;
    setWorking(true);
    setMessage("");
    setCode("");

    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), 60000);
    const owner = clerk.session?.id;

    try {
      const configured = import.meta.env.VITE_API_BASE_URL?.trim();
      if (!configured) {
        throw new Error("VITE_API_BASE_URL is missing from this frontend's environment.");
      }

      const base = new URL(configured);
      const local =
        import.meta.env.DEV &&
        base.protocol === "http:" &&
        ["localhost", "127.0.0.1"].includes(base.hostname);

      if (base.protocol !== "https:" && !local) {
        throw new Error("The backend URL is invalid.");
      }

      async function post(route: string, body: object) {
        const token = await getToken();
        if (!token) throw new Error("Please sign in again.");

        const response = await fetch(
          base.toString().replace(/\/+$/, "") + route,
          {
            method: "POST",
            credentials: "include",
            signal: controller.signal,
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: "Bearer " + token,
              "X-Application-Key": applicationKey,
            },
            body: JSON.stringify(body),
          }
        );

        const data: unknown = await response.json().catch(() => null);

        if (clerk.session?.id !== owner) {
          throw new Error("Your account changed. Run the connection test again.");
        }

        if (!record(data)) {
          throw new Error("The backend returned an invalid response.");
        }

        if (!response.ok || data.success !== true) {
          const errorCode = typeof data.code === "string"
            ? data.code
            : "HTTP_" + response.status;

          setCode(errorCode);

          if (
            response.status === 403 &&
            errorCode === "APPLICATION_ACCESS_REQUIRED"
          ) {
            const onboardingPath = {
              hospital: "/onboarding/hospital-clinic",
              lab: "/onboarding/laboratory",
              insurance: "/onboarding/insurance",
            }[applicationKey];

            window.location.replace(onboardingPath);

            throw new Error(
              "Your identity is synced. This account needs organisation onboarding for " +
              applicationKey + "."
            );
          }

          throw new Error(
            response.status === 401
              ? "The backend could not verify your Clerk session."
              : response.status === 403
                ? "The backend denied access. Check the response code below."
                : response.status === 409
                  ? "The backend requires account linking or synchronisation. Please check with the backend engineer."
                  : "The backend connection failed (HTTP " + response.status + ")."
          );
        }

        return data;
      }

      await post("/api/v2/auth/sync", {});
      const response = await post("/api/v2/auth/session", { applicationKey });
      const data = response.data;

      if (
        !record(data) ||
        typeof data.accessToken !== "string" ||
        !data.accessToken.trim()
      ) {
        throw new Error(
          "Session creation succeeded, but the access-token response needs to be checked."
        );
      }

      const dashboard = {
        hospital: "/dashboard",
        lab: "/lab/dashboard",
        insurance: "/insurance/dashboard",
      }[applicationKey];
      window.location.replace(dashboard);
      setMessage(
        "User sync and " + applicationKey +
        " session creation succeeded. Dashboard session wiring is the next step."
      );
    } catch (error) {
      if (clerk.session?.id !== owner) return;

      setMessage(
        controller.signal.aborted
          ? "The request timed out. Try again."
          : error instanceof TypeError
            ? "The browser could not reach the backend. Check the console for a network or CORS error."
            : error instanceof Error
              ? error.message
              : "Unable to test the connection."
      );
    } finally {
      window.clearTimeout(timer);
      busy.current = false;
      setWorking(false);
    }
  }

  return (
    <section className="space-y-4 rounded-2xl border border-[#E4EFED] bg-white p-6">
      <h1 className="text-xl font-semibold text-[#17313A]">You are signed in</h1>
      <p className="text-sm text-[#65777C]">
        Clerk authentication succeeded. Check your organisation access before continuing.
      </p>
      <button
        type="button"
        disabled={working}
        onClick={() => void test()}
        className="w-full rounded-lg bg-[#159A92] px-4 py-3 text-sm font-medium text-white disabled:opacity-50"
      >
        {working ? "Checking…" : "Test backend connection"}
      </button>
      {message && <p role="status" className="text-sm text-[#17313A]">{message}</p>}
      {code && <p className="text-xs text-[#65777C]">Response code: {code}</p>}
      <button
        type="button"
        disabled={working}
        onClick={() => {
          void clerk.signOut({ redirectUrl: "/login" }).catch(() =>
            setMessage("Unable to sign out. Try again.")
          );
        }}
        className="text-sm font-medium text-[#159A92]"
      >
        Sign out
      </button>
    </section>
  );
}
